import { createContext, useContext } from "react";

export const FREE_PLAN_SLUGS = new Set(["u:free_user", "o:free_org"]);

export function hasPaidOrgPlan(plan: string | null | undefined): boolean {
  return Boolean(plan?.startsWith("o:")) && !FREE_PLAN_SLUGS.has(plan ?? "");
}

/**
 * Whether the active org qualifies for org features (shared gallery, assets).
 * True when the org is on a paid plan OR the active org is flagged
 * billing_exempt in its Clerk public metadata (free internal/demo orgs).
 */
export function hasOrgAccess(billingStatus: BillingStatus | null): boolean {
  return (
    hasPaidOrgPlan(billingStatus?.active_plan) ||
    Boolean(billingStatus?.org_exempt)
  );
}

export interface BillingStatus {
  /** Paid plan, billing exemption, or exempt org — ignores the allowance. */
  has_active_subscription: boolean;
  /**
   * Gate for generation: active subscription OR iterations left in this
   * month's free allowance. Note this no longer gates *routes* — an
   * out-of-iterations user stays on /, /results and /refine and sees the
   * out-of-iterations overlay there rather than being sent to /pricing.
   */
  has_design_access: boolean;
  active_plan: string | null;
  billing_exempt: boolean;
  org_exempt: boolean;
  is_admin: boolean;
  /** Iterations spent this calendar month (from GET /api/users/me). */
  iterations_used: number;
  /** Iterations a free account may spend per calendar month. */
  iterations_limit: number;
}

/**
 * Iterations the user has left this month, floored at 0 so an account that
 * overshot the limit (concurrent requests can) reads as empty, not negative.
 *
 * Meaningless for subscribers — check `has_active_subscription` first.
 */
export function iterationsRemaining(
  billingStatus: BillingStatus | null,
): number {
  if (!billingStatus) return 0;
  return Math.max(
    0,
    billingStatus.iterations_limit - billingStatus.iterations_used,
  );
}

/**
 * Whether to show the out-of-iterations overlay/modal: a free account that has
 * spent its allowance. False while billing is still loading, so the overlay
 * never flashes over a page that turns out to be fine.
 */
export function isOutOfIterations(
  billingStatus: BillingStatus | null,
  isBillingLoading: boolean,
): boolean {
  if (isBillingLoading || !billingStatus) return false;
  return !billingStatus.has_design_access;
}

interface BillingStatusContextValue {
  billingStatus: BillingStatus | null;
  isBillingLoading: boolean;
}

export const BillingStatusContext = createContext<BillingStatusContextValue>({
  billingStatus: null,
  isBillingLoading: false,
});

export function useBillingStatus() {
  return useContext(BillingStatusContext);
}
